import { z } from 'zod';
import type {
  CarouselNode,
  ColumnChild,
  ColumnNode,
  ContainerChild,
  ContainerNode,
  LabelNode,
  PageLayout,
  RowNode,
  SlotNode,
} from '../types/page-layout';
import { LABEL_PRESETS, SLOT_PRESETS } from '../types/page-layout';

// Reuses slug ruleset from group-content (Thai or lowercase Latin + digits + dashes).
const slugRx = /^[฀-๿a-z0-9]+(?:-[฀-๿a-z0-9]+)*$/u;

const nodeIdSchema = z
  .string()
  .trim()
  .min(1)
  .max(60)
  .regex(/^[a-z0-9][a-z0-9-]*$/i, 'node id ใช้ได้แค่ a-z, 0-9, -');

const groupSlugSchema = z
  .string()
  .trim()
  .min(1, 'กรุณาเลือก Group Content')
  .max(80)
  .regex(slugRx, 'slug ไม่ถูกต้อง');

const columnSpanSchema = z.number().int().min(1).max(12);
const rowGapSchema = z.number().int().min(0).max(5);

/** Preset is enum-validated at the schema level. Legacy `'thumb'` is
 *  normalized to `'thumb-sm'` by the auto-migrator before Zod sees it,
 *  so the schema can stay strict against SLOT_PRESETS. */
const presetSchema = z.enum(SLOT_PRESETS);

/* ---------- Leaves ---------- */

const slotSchema: z.ZodType<SlotNode> = z.object({
  id: nodeIdSchema,
  type: z.literal('slot'),
  groupSlug: groupSlugSchema,
  /** 0-based item position in the group. Capped at 19 to match the max
   *  `group.maxCount`; renderer returns null if the group runs out. */
  itemIndex: z.number().int().min(0).max(19),
  preset: presetSchema,
});

const carouselSchema: z.ZodType<CarouselNode> = z.object({
  id: nodeIdSchema,
  type: z.literal('carousel'),
  groupSlug: groupSlugSchema,
  preset: presetSchema,
});

const labelSchema: z.ZodType<LabelNode> = z.object({
  id: nodeIdSchema,
  type: z.literal('label'),
  text: z.string().trim().min(1, 'กรอกข้อความ').max(500),
  // Optional URL — admin may leave empty. Accept either http/https or a
  // root-relative path so internal links work without baking the public
  // origin into the layout JSON.
  url: z
    .string()
    .trim()
    .max(2000)
    .regex(/^(https?:\/\/|\/)/u, 'URL ต้องขึ้นต้นด้วย http(s):// หรือ /')
    .optional(),
  preset: z.enum(LABEL_PRESETS),
});

/* ---------- Recursive node schemas ----------
 *
 * Zod recursion uses z.lazy + an explicit ZodType<T> annotation on each
 * variable so the cycle resolves at type-check time. Discriminated unions
 * inside lazy resolutions fight TS's variance, so we fall back to z.union
 * for the children unions (Zod still narrows by .type literal at runtime).
 */

const containerSchema: z.ZodType<ContainerNode> = z.lazy(() =>
  z.object({
    id: nodeIdSchema,
    type: z.literal('container'),
    variant: z.enum(['fluid', 'fixed']),
    children: z.array(containerChildSchema).max(20, 'children ไม่เกิน 20'),
  }),
);

const columnSchema: z.ZodType<ColumnNode> = z.lazy(() =>
  z.object({
    id: nodeIdSchema,
    type: z.literal('column'),
    span: columnSpanSchema,
    children: z.array(columnChildSchema).max(20, 'children ไม่เกิน 20'),
  }),
);

const rowSchema: z.ZodType<RowNode> = z.lazy(() =>
  z
    .object({
      id: nodeIdSchema,
      type: z.literal('row'),
      gap: rowGapSchema,
      children: z.array(columnSchema).max(12, 'row มี column ได้ไม่เกิน 12'),
    })
    .refine((row) => row.children.reduce((sum, col) => sum + col.span, 0) <= 12, {
      message: 'ผลรวม span ของ column ใน row ต้อง ≤ 12',
      path: ['children'],
    }),
);

const containerChildSchema: z.ZodType<ContainerChild> = z.lazy(() =>
  z.union([containerSchema, rowSchema, slotSchema, carouselSchema, labelSchema]),
);

const columnChildSchema: z.ZodType<ColumnChild> = z.lazy(() =>
  z.union([containerSchema, rowSchema, slotSchema, carouselSchema, labelSchema]),
);

/* ---------- Top-level page layout ---------- */

export const pageLayoutSchema: z.ZodType<PageLayout> = z.object({
  version: z.literal(3),
  root: containerSchema,
});

export type PageLayoutInput = z.infer<typeof pageLayoutSchema>;
